import React, { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Badge, Nav, NavItem, NavLink as RsNavLink } from "reactstrap";
import { connect } from "react-redux";
import classNames from "classnames";
import navAuth from "./_navAuth";
import navBarTeacher from "./_navBarTeacher";
import SidebarFooter from "../SidebarFooter/SidebarFooter";
import SidebarForm from "../SidebarForm/SidebarForm";
import SidebarHeader from "../SidebarHeader/SidebarHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OrganisationSearchBarContainer from "../Inputs/OrganisationSearchBarContainer";
import { GetTeacherInformation } from "../../actions/teacherQueries";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { injectIntl } from "react-intl";

export function useSidebarResize(min = 180, max = 800) {
  const frame = useRef(null);
  const latestWidth = useRef(null);
  const observer = useRef(null);

  const setSidebarWidth = (px) =>
    document.documentElement.style.setProperty("--sidebar-width", `${px}px`);

  const cancelFrame = () => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!document.documentElement.style.getPropertyValue("--sidebar-width")) {
      const saved = parseFloat(localStorage.getItem("sidebarWidth"));
      const width = !isNaN(saved) ? Math.max(min, Math.min(max, saved)) : 240;
      setSidebarWidth(width);
    }

    const setUp = () => {
      const resizer = document.querySelector(".sidebar-resizer");
      const sidebar = resizer?.closest(".sidebar");

      if (!resizer || !sidebar) return false;

      let startX = 0;
      let startWidth = 0;

      const onPointerDown = (e) => {
        startX = e.clientX;
        startWidth = sidebar.getBoundingClientRect().width;

        document.body.classList.add("sidebar-resizing");
        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
      };

      const onPointerMove = (e) => {
        const dx = e.clientX - startX;
        const newWidth = Math.max(min, Math.min(max, startWidth + dx));
        latestWidth.current = newWidth;

        cancelFrame();
        frame.current = requestAnimationFrame(() => setSidebarWidth(newWidth));
      };

      const onPointerUp = () => {
        document.body.classList.remove("sidebar-resizing");
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);

        cancelFrame();
        if (latestWidth.current != null) {
          localStorage.setItem("sidebarWidth", latestWidth.current);
        }
      };

      resizer.addEventListener("pointerdown", onPointerDown);

      return () => {
        resizer.removeEventListener("pointerdown", onPointerDown);
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        cancelFrame();
      };
    };

    let cleanup = setUp();

    if (!cleanup) {
      observer.current = new MutationObserver(() => {
        cleanup = setUp();
        if (cleanup) observer.current.disconnect();
      });
      observer.current.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (cleanup) cleanup();
      observer.current?.disconnect();
      cancelFrame();
    };
  }, [min, max]);
}

function Sidebar(props) {
  useSidebarResize(180, 800);

  const role = props.auth?.auth?.roles;
  const isTeacher = role === "Teacher";
  const isStudent = role === "Student";

  const teacherInfo = GetTeacherInformation({
    enabled: isTeacher,
  });

  const getIndent = (type) => {
    switch (type) {
      case "program":
        return "1rem";
      case "class":
        return "2.8rem";
      case "teacher":
      case "student":
        return "4.3rem";
      default:
        return "0.5rem";
    }
  };

  var handleClick = (e) => {
    e.preventDefault();
    const dropdown = e.currentTarget.closest(".nav-dropdown");
    if (dropdown) dropdown.classList.toggle("open");
  };

  var activeRoute = (routeName, props) => {
    return props.location.pathname.indexOf(routeName) > -1
      ? "nav-item nav-dropdown open"
      : "nav-item nav-dropdown";
  };

  var hideMobile = () => {
    if (document.body.classList.contains("sidebar-mobile-show")) {
      document.body.classList.toggle("sidebar-mobile-show");
    }
  };

  var showStudentName = (programName, stud) => {
    const isInactive = !stud.activatedAccount;
    const className = isInactive ? "text-gray-300" : undefined;

    if (stud.requiredLicenseIsMissing && stud.hasValidAplLicense) {
      const titleId = isInactive
        ? "Student_HasAplButNeedProgramLicenseAndActivation"
        : "Student_HasAplButNeedProgramLicense";

      return (
        <span
          className={className}
          title={props.intl.formatMessage({ id: titleId }, { programName })}
        >
          {stud.name}
          <InfoOutlinedIcon className="license-warning-icon" />
        </span>
      );
    }

    if (stud.requiredLicenseIsMissing) {
      const titleId = isInactive
        ? "Student_NeedLicenseAndActivation"
        : "Student_NeedLicense";

      return (
        <span
          className={className}
          title={props.intl.formatMessage({ id: titleId }, { programName })}
        >
          {stud.name}
          <WarningAmberIcon className="text-warning license-warning-icon" />
        </span>
      );
    }

    if (isInactive) {
      return (
        <span
          className={className}
          title={props.intl.formatMessage({ id: "Student_NeedActivation" })}
        >
          {stud.name}
        </span>
      );
    }

    return stud.name;
  };

  var showProgramName = (prog) => {
    const programName = prog.name;
    if (prog.hasExpired) {
      return (
        <span
          title={props.intl.formatMessage(
            { id: "Program_NeedLicense" },
            { programName }
          )}
        >
          {programName}
          <WarningAmberIcon className="text-warning license-warning-icon" />
        </span>
      );
    }
    return programName;
  };

  var OrgStructure = (props) => {
    var orgStructure = [];
    if (teacherInfo?.data?.programmes !== undefined) {
      teacherInfo.data.programmes.forEach((prog) => {
        var classes = [];
        prog.classes.forEach((cl) => {
          var usersInClass = [];
          var sortingListUsersInClass = [];
          cl.students.forEach((stud) => {
            var student = {
              name: showStudentName(prog.name, stud),
              sortName: stud.name ?? "",
              url:
                "/teacher/programmeview/" +
                prog.id +
                "/" +
                prog.name +
                "/classview/" +
                cl.id +
                "/" +
                cl.name +
                "/studentview/" +
                stud.id +
                "/" +
                stud.name,
              icon: "user",
              type: "student",
            };
            sortingListUsersInClass.push(student);
          });
          sortingListUsersInClass = sortingListUsersInClass.sort((a, b) =>
            a.sortName.trim().localeCompare(b.sortName.trim(), "sv", {
              sensitivity: "base",
              numeric: true,
            })
          );
          sortingListUsersInClass.forEach((user) => usersInClass.push(user));
          sortingListUsersInClass = [];
          cl.teachers.forEach((te) => {
            var teacher = {
              name: te.name,
              sortName: te.name ?? "",
              url:
                "/teacher/programmeview/" +
                prog.id +
                "/" +
                prog.name +
                "/classview/" +
                cl.id +
                "/" +
                cl.name +
                "/teacherview/" +
                te.id +
                "/" +
                te.name,
              icon: "graduation-cap",
              type: "teacher",
            };
            sortingListUsersInClass.push(teacher);
          });
          sortingListUsersInClass.sort((a, b) =>
            a.sortName.trim().localeCompare(b.sortName.trim(), "sv", {
              sensitivity: "base",
              numeric: true,
            })
          );
          sortingListUsersInClass.forEach((user) => usersInClass.push(user));
          var classo = {
            name: cl.name,
            sortName: cl.name ?? "",
            url:
              "/teacher/programmeview/" +
              prog.id +
              "/" +
              prog.name +
              "/classview/" +
              cl.id +
              "/" +
              cl.name,
            icon: "users",
            type: "class",
            children: usersInClass,
          };
          classes.push(classo);
        });
        classes = classes.sort((a, b) =>
          a.sortName.trim().localeCompare(b.sortName.trim(), "sv", {
            sensitivity: "base",
            numeric: true,
          })
        );
        var programme = {
          name: showProgramName(prog),
          sortName: prog.name ?? "",
          url: "/teacher/programmeview/" + prog.id + "/" + prog.name,
          icon: "briefcase",
          children: classes,
          type: "program",
        };

        orgStructure.push(programme);
      });
    }
    orgStructure = orgStructure.sort((a, b) =>
      a.sortName.trim().localeCompare(b.sortName.trim(), "sv", {
        sensitivity: "base",
        numeric: true,
      })
    );
    return orgStructure;
  };

  // badge addon to NavItem
  const badge = (badge) => {
    if (badge) {
      const classes = classNames(badge.class);
      return (
        <Badge className={classes} color={badge.variant}>
          {badge.text}
        </Badge>
      );
    }
    return null;
  };

  // simple wrapper for nav-title item
  const wrapper = (item) => {
    return item.wrapper && item.wrapper.element
      ? React.createElement(
          item.wrapper.element,
          item.wrapper.attributes,
          item.name
        )
      : item.name;
  };

  // nav list section title
  const title = (title, key) => {
    const classes = classNames("nav-title", title.class);
    return (
      <li key={title + key} className={classes}>
        {wrapper(title)}{" "}
      </li>
    );
  };

  // nav list divider
  const divider = (divider, key) => {
    const classes = classNames("divider", divider.class);
    return <li key={key} className={classes} />;
  };

  // nav label with nav link
  const navLabel = (item, key) => {
    const classes = {
      item: classNames("hidden-cn", item.class),
      link: classNames("nav-label", item.class ? item.class : ""),
      icon: classNames(
        !item.icon ? "fa fa-circle" : item.icon,
        item.label.variant ? `text-${item.label.variant}` : "",
        item.label.class ? item.label.class : ""
      ),
    };
    return navLink(item, key, classes);
  };

  // nav item with nav link
  const navItem = (item, key) => {
    const classes = {
      item: classNames(item.class),
      link: classNames(
        "nav-link",
        item.variant ? `nav-link-${item.variant}` : ""
      ),
      icon: classNames(item.icon),
    };
    return navLink(item, key, classes);
  };

  // nav link
  const navLink = (item, key, classes) => {
    const url = item.url ? item.url : "";
    const indent = getIndent(item.type);

    return (
      <NavItem key={key} className={classes.item}>
        {isExternal(url) ? (
          <RsNavLink href={url} className={classes.link} active>
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </RsNavLink>
        ) : url !== null && url !== "" ? (
          <NavLink
            to={{
              pathname: url,
              state: { level: item.type, name: item.name },
            }}
            style={{
              paddingLeft: indent,
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
            }}
            className={classes.link}
            activeClassName="active"
            onClick={hideMobile}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </NavLink>
        ) : (
          <span
            style={{
              paddingLeft: indent,
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
              cursor: "default",
              maxWidth: "240px",
            }}
            className={classes.link}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </span>
        )}
      </NavItem>
    );
  };

  // Dropdown and navlink
  const navItemAndDropdown = (item, key) => {
    const classes = {
      item: classNames(item.class),
      link: classNames(
        "nav-link",
        item.variant ? `nav-link-${item.variant}` : ""
      ),
      icon: classNames(item.icon),
    };
    const url = item.url ? item.url : "";
    const indent = getIndent(item.type);

    return (
      <li
        key={item.name + key}
        className={`nav-dropdown ${activeRoute(item.url, props)}`}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            paddingLeft: `${indent}`,
          }}
        >
          <a
            href="# "
            style={{
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "0",
              padding: "0",
              minWidth: "20px",
            }}
            className="nav-link custom-sidebar-nav-dropdown-toggle"
            onClick={(event) => handleClick(event)}
          >
            {" "}
          </a>
          <NavLink
            to={{
              pathname: url,
              state: { level: item.type, name: item.name },
            }}
            className={classes.link}
            style={{
              flex: 1,
              paddingTop: "0.2rem",
              paddingBottom: "0.2rem",
              paddingLeft: item.type === "class" ? "1%" : "0",
            }}
            activeClassName="active"
            onClick={hideMobile}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </NavLink>
        </div>

        <ul className="nav-dropdown-items" style={{ margin: 0, padding: 0 }}>
          {navList(item.children)}
        </ul>
      </li>
    );
  };

  // nav type
  const navType = (item, idx) =>
    item.title
      ? title(item, idx)
      : item.divider
      ? divider(item, idx)
      : item.label
      ? navLabel(item, idx)
      : item.children && (item.type === "class" || item.type === "program")
      ? navItemAndDropdown(item, idx)
      : navItem(item, idx);

  // nav list
  const navList = (items) => {
    return items.map((item, index) =>
      !item.isProgramsAndClasses ? (
        navType(item, index)
      ) : (
        <ul
          key={item.name + index}
          style={{ listStyleType: "none" }}
          className="ps-0"
        >
          {" "}
          {item.programsAndClasses.map((orgItem) => navType(orgItem, index))}
        </ul>
      )
    );
  };

  const isExternal = (url) => {
    const link = url ? url.substring(0, 4) : "";
    return link === "http";
  };

  let NavBar = <Nav />;

  if (role === null) {
    return <div></div>;
  }

  // Navlists
  //Student
  if (isStudent) {
    NavBar = () => <Nav>{navList(navAuth.items)}</Nav>;
  }
  //Teacher
  else if (isTeacher) {
    if (teacherInfo.isLoading) {
      return null;
    }

    //Get query
    var teacherNavBar = navBarTeacher(OrgStructure(teacherInfo.data));
    NavBar = () => <Nav>{navList(teacherNavBar)}</Nav>;
  }
  //Superuser/Admin
  else {
    NavBar = () => <Nav></Nav>;
  }

  // sidebar-nav root
  return (
    <div className="sidebar">
      <SidebarHeader />
      <SidebarForm />

      {isTeacher ? (
        <OrganisationSearchBarContainer
          teacherInfo={teacherInfo}
          searchCriterias={["students", "classes", "programs"]}
          placeHolder="Sök elev, e-post, klass eller program..."
          theme="dark"
        />
      ) : (
        ""
      )}

      <nav className="sidebar-nav">
        <NavBar />
      </nav>
      <SidebarFooter />

      <div className="sidebar-resizer" />
    </div>
  );
}
// här kan det bli problem med inlogg
function mapStateToProps(store) {
  return {
    authenticated: store.auth.authenticated,
  };
}

export default injectIntl(connect(mapStateToProps)(Sidebar));
